// src/pages/AdminBedsPage.js
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Alert, Badge, Button, Card, Col, Form, Modal, Row, Spinner, Table,
} from "react-bootstrap";
import api from "../api";

const BED_SIZES = ["Twin", "Full", "Frames"];

const EMPTY_ORDER = {
  size: "Twin",
  frames_ordered: "",
  mattresses_ordered: "",
  box_springs_ordered: "",
  date_ordered: "",
  source: "",
  notes: "",
};

const EMPTY_INITIAL = {
  twin_mattresses: "",
  twin_box_springs: "",
  full_mattresses: "",
  full_box_springs: "",
  frames: "",
};

export default function AdminBedsPage() {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState("");
  const [actionError, setActionError] = useState("");
  const [saving, setSaving]   = useState(false);

  // modal: "order-add" | "order-edit" | "order-receive" | "order-delete" | "initial"
  const [modal, setModal]   = useState(null);
  const [target, setTarget] = useState(null);
  const [form, setForm]     = useState({});

  const load = useCallback(async () => {
    setError("");
    try {
      const res = await api.get("/admin/beds");
      setData(res.data);
    } catch {
      setError("Failed to load bed inventory.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  function field(key) {
    return (e) => setForm((p) => ({ ...p, [key]: e.target.value }));
  }

  function closeModal() {
    setModal(null);
    setTarget(null);
    setActionError("");
  }

  // ── Initial Inventory ──────────────────────────────────────────────────────

  function openInitial() {
    const init = data?.initial || {};
    setForm({
      twin_mattresses:  init.Twin?.mattresses_received  ?? "",
      twin_box_springs: init.Twin?.box_springs_received ?? "",
      full_mattresses:  init.Full?.mattresses_received  ?? "",
      full_box_springs: init.Full?.box_springs_received ?? "",
      frames:           init.Frames?.frames_received    ?? "",
    });
    setActionError("");
    setModal("initial");
  }

  async function handleSaveInitial() {
    setSaving(true);
    setActionError("");
    try {
      const init = data?.initial || {};

      for (const size of ["Twin", "Full"]) {
        const key = size.toLowerCase();
        const mattresses  = Number(form[`${key}_mattresses`])  || 0;
        const box_springs = Number(form[`${key}_box_springs`]) || 0;
        const existing    = init[size];
        if (existing) {
          await api.put(`/admin/beds/${existing.id}`, {
            mattresses_received: mattresses,
            box_springs_received: box_springs,
          });
        } else {
          await api.post("/admin/beds", {
            size,
            is_initial: true,
            mattresses_received: mattresses,
            box_springs_received: box_springs,
          });
        }
      }

      const frames          = Number(form.frames) || 0;
      const existingFrames  = init.Frames;
      if (existingFrames) {
        await api.put(`/admin/beds/${existingFrames.id}`, { frames_received: frames });
      } else {
        await api.post("/admin/beds", { size: "Frames", is_initial: true, frames_received: frames });
      }

      await load();
      closeModal();
    } catch (err) {
      setActionError(err.response?.data?.error || "Save failed.");
    } finally {
      setSaving(false);
    }
  }

  // ── Orders ─────────────────────────────────────────────────────────────────

  function openAddOrder() {
    setForm({ ...EMPTY_ORDER });
    setActionError("");
    setTarget(null);
    setModal("order-add");
  }

  function openEditOrder(order) {
    setForm({
      size:                order.size,
      frames_ordered:      order.frames_ordered      ?? "",
      mattresses_ordered:  order.mattresses_ordered  ?? "",
      box_springs_ordered: order.box_springs_ordered ?? "",
      date_ordered:        order.date_ordered        ?? "",
      source:              order.source              ?? "",
      notes:               order.notes               ?? "",
    });
    setActionError("");
    setTarget(order);
    setModal("order-edit");
  }

  function openReceiveOrder(order) {
    setForm({
      frames_received:      order.frames_received      ?? order.frames_ordered      ?? "",
      mattresses_received:  order.mattresses_received  ?? order.mattresses_ordered  ?? "",
      box_springs_received: order.box_springs_received ?? order.box_springs_ordered ?? "",
      date_received:        order.date_received        ?? new Date().toISOString().slice(0, 10),
    });
    setActionError("");
    setTarget(order);
    setModal("order-receive");
  }

  function openDeleteOrder(order) {
    setActionError("");
    setTarget(order);
    setModal("order-delete");
  }

  async function handleSaveOrder() {
    setSaving(true);
    setActionError("");
    const isFrames = form.size === "Frames";
    const payload = {
      size:                form.size,
      frames_ordered:      isFrames  ? (form.frames_ordered      !== "" ? Number(form.frames_ordered)      : null) : null,
      mattresses_ordered:  !isFrames ? (form.mattresses_ordered  !== "" ? Number(form.mattresses_ordered)  : null) : null,
      box_springs_ordered: !isFrames ? (form.box_springs_ordered !== "" ? Number(form.box_springs_ordered) : null) : null,
      date_ordered:        form.date_ordered || null,
      source:              form.source       || null,
      notes:               form.notes        || null,
    };
    try {
      if (modal === "order-add") {
        await api.post("/admin/beds", payload);
      } else {
        await api.put(`/admin/beds/${target.id}`, payload);
      }
      await load();
      closeModal();
    } catch (err) {
      setActionError(err.response?.data?.error || "Save failed.");
    } finally {
      setSaving(false);
    }
  }

  async function handleReceiveOrder() {
    setSaving(true);
    setActionError("");
    const isFrames = target?.size === "Frames";
    try {
      await api.put(`/admin/beds/${target.id}`, {
        frames_received:      isFrames  ? (form.frames_received      !== "" ? Number(form.frames_received)      : null) : null,
        mattresses_received:  !isFrames ? (form.mattresses_received  !== "" ? Number(form.mattresses_received)  : null) : null,
        box_springs_received: !isFrames ? (form.box_springs_received !== "" ? Number(form.box_springs_received) : null) : null,
        date_received:        form.date_received || null,
      });
      await load();
      closeModal();
    } catch (err) {
      setActionError(err.response?.data?.error || "Save failed.");
    } finally {
      setSaving(false);
    }
  }

  async function handleDeleteOrder() {
    setSaving(true);
    setActionError("");
    try {
      await api.delete(`/admin/beds/${target.id}`);
      await load();
      closeModal();
    } catch (err) {
      setActionError(err.response?.data?.error || "Delete failed.");
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <div className="p-4"><Spinner animation="border" /></div>;
  if (error)   return <Alert variant="danger" className="m-3">{error}</Alert>;

  const { summary, initial, orders } = data;
  const hasInitial     = BED_SIZES.some((s) => initial[s]);
  const pendingOrders  = orders.filter((o) => !o.date_received);
  const receivedOrders = orders.filter((o) => o.date_received);
  const isEditingFrames = form.size === "Frames";

  return (
    <div className="p-3">
      <div className="d-flex align-items-center mb-4 gap-2">
        <Link to="/admin" className="btn btn-sm btn-outline-secondary">
          <i className="bi bi-arrow-left" /> Admin
        </Link>
        <h4 className="mb-0">
          <i className="bi bi-house-fill me-2" />
          Bed Inventory
        </h4>
      </div>

      {actionError && (
        <Alert variant="danger" dismissible onClose={() => setActionError("")}>
          {actionError}
        </Alert>
      )}

      {/* ── Current Stock ── */}
      <div className="d-flex align-items-center justify-content-between mb-3">
        <h5 className="mb-0">Current Stock</h5>
        <Button size="sm" variant={hasInitial ? "outline-secondary" : "outline-primary"} onClick={openInitial}>
          <i className="bi bi-pencil-square me-1" />
          {hasInitial ? "Edit Initial Inventory" : "Set Initial Inventory"}
        </Button>
      </div>

      <Row className="g-3 mb-4">
        {["Twin", "Full"].map((size) => {
          const s = summary[size] || { mattresses: 0, box_springs: 0 };
          return (
            <Col key={size} xs={12} sm={6} md={4}>
              <Card className="h-100">
                <Card.Body>
                  <Card.Title>{size}</Card.Title>
                  <Row className="text-center mt-2">
                    <Col>
                      <div className="display-6 fw-bold">{s.mattresses}</div>
                      <div className="text-muted small">Mattresses</div>
                    </Col>
                    <Col>
                      <div className="display-6 fw-bold">{s.box_springs}</div>
                      <div className="text-muted small">Box Springs</div>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Col>
          );
        })}
        <Col xs={12} sm={6} md={4}>
          <Card className="h-100">
            <Card.Body>
              <Card.Title>Frames</Card.Title>
              <Row className="text-center mt-2">
                <Col>
                  <div className="display-6 fw-bold">{summary.Frames?.frames ?? 0}</div>
                  <div className="text-muted small">Frames (Universal)</div>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* ── Pending Orders ── */}
      <div className="d-flex align-items-center justify-content-between mb-2">
        <h5 className="mb-0">
          Pending Orders
          {pendingOrders.length > 0 && (
            <Badge bg="warning" text="dark" className="ms-2">{pendingOrders.length}</Badge>
          )}
        </h5>
        <Button size="sm" variant="primary" onClick={openAddOrder}>
          <i className="bi bi-plus-lg me-1" />New Order
        </Button>
      </div>

      {pendingOrders.length === 0 ? (
        <p className="text-muted mb-4">No pending orders.</p>
      ) : (
        <Table bordered hover responsive size="sm" className="mb-4">
          <thead className="table-warning">
            <tr>
              <th>Size</th>
              <th>Matt. Ord.</th>
              <th>Box Spr. Ord.</th>
              <th>Frames Ord.</th>
              <th>Date Ordered</th>
              <th>Source</th>
              <th>Notes</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {pendingOrders.map((o) => (
              <tr key={o.id}>
                <td>{o.size}</td>
                <td>{o.size !== "Frames" ? (o.mattresses_ordered  ?? "—") : "—"}</td>
                <td>{o.size !== "Frames" ? (o.box_springs_ordered ?? "—") : "—"}</td>
                <td>{o.size === "Frames" ? (o.frames_ordered      ?? "—") : "—"}</td>
                <td>{o.date_ordered ?? "—"}</td>
                <td>{o.source       || "—"}</td>
                <td>{o.notes        || "—"}</td>
                <td className="text-nowrap">
                  <Button size="sm" variant="success" className="me-1" onClick={() => openReceiveOrder(o)}>
                    Mark Received
                  </Button>
                  <Button size="sm" variant="outline-primary" className="me-1" onClick={() => openEditOrder(o)}>
                    <i className="bi bi-pencil" />
                  </Button>
                  <Button size="sm" variant="outline-danger" onClick={() => openDeleteOrder(o)}>
                    <i className="bi bi-trash" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      {/* ── Received Orders ── */}
      {receivedOrders.length > 0 && (
        <>
          <h5 className="mb-2">Received Orders</h5>
          <Table bordered hover responsive size="sm" className="mb-4">
            <thead className="table-light">
              <tr>
                <th>Size</th>
                <th>Matt. Ord.</th>
                <th>Matt. Rec.</th>
                <th>Box Spr. Ord.</th>
                <th>Box Spr. Rec.</th>
                <th>Frames Ord.</th>
                <th>Frames Rec.</th>
                <th>Date Ordered</th>
                <th>Date Received</th>
                <th>Source</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {receivedOrders.map((o) => (
                <tr key={o.id}>
                  <td>{o.size}</td>
                  <td>{o.size !== "Frames" ? (o.mattresses_ordered   ?? "—") : "—"}</td>
                  <td>{o.size !== "Frames" ? (o.mattresses_received  ?? "—") : "—"}</td>
                  <td>{o.size !== "Frames" ? (o.box_springs_ordered  ?? "—") : "—"}</td>
                  <td>{o.size !== "Frames" ? (o.box_springs_received ?? "—") : "—"}</td>
                  <td>{o.size === "Frames" ? (o.frames_ordered       ?? "—") : "—"}</td>
                  <td>{o.size === "Frames" ? (o.frames_received      ?? "—") : "—"}</td>
                  <td>{o.date_ordered  ?? "—"}</td>
                  <td>{o.date_received ?? "—"}</td>
                  <td>{o.source        || "—"}</td>
                  <td className="text-nowrap">
                    <Button size="sm" variant="outline-primary" className="me-1" onClick={() => openEditOrder(o)}>
                      <i className="bi bi-pencil" />
                    </Button>
                    <Button size="sm" variant="outline-danger" onClick={() => openDeleteOrder(o)}>
                      <i className="bi bi-trash" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </>
      )}

      {/* ── Set / Edit Initial Inventory Modal ── */}
      <Modal show={modal === "initial"} onHide={closeModal}>
        <Modal.Header closeButton>
          <Modal.Title>{hasInitial ? "Edit Initial Inventory" : "Set Initial Inventory"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {actionError && <Alert variant="danger">{actionError}</Alert>}
          <p className="text-muted small">
            Enter your starting on-hand counts before you began tracking orders here.
            This is a one-time baseline — use orders for any new stock after this.
          </p>

          {["Twin", "Full"].map((size) => {
            const key = size.toLowerCase();
            return (
              <div key={size} className="mb-3">
                <div className="fw-semibold mb-2">{size}</div>
                <Row className="g-2">
                  <Col>
                    <Form.Label className="small">Mattresses</Form.Label>
                    <Form.Control
                      type="number" min="0"
                      value={form[`${key}_mattresses`] ?? ""}
                      onChange={field(`${key}_mattresses`)}
                    />
                  </Col>
                  <Col>
                    <Form.Label className="small">Box Springs</Form.Label>
                    <Form.Control
                      type="number" min="0"
                      value={form[`${key}_box_springs`] ?? ""}
                      onChange={field(`${key}_box_springs`)}
                    />
                  </Col>
                </Row>
              </div>
            );
          })}

          <div className="mb-1">
            <div className="fw-semibold mb-2">Frames (Universal)</div>
            <Row className="g-2">
              <Col xs={6}>
                <Form.Label className="small">Frames</Form.Label>
                <Form.Control
                  type="number" min="0"
                  value={form.frames ?? ""}
                  onChange={field("frames")}
                />
              </Col>
            </Row>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeModal} disabled={saving}>Cancel</Button>
          <Button variant="primary" onClick={handleSaveInitial} disabled={saving}>
            {saving ? <Spinner animation="border" size="sm" /> : "Save"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ── Add / Edit Order Modal ── */}
      <Modal show={modal === "order-add" || modal === "order-edit"} onHide={closeModal}>
        <Modal.Header closeButton>
          <Modal.Title>{modal === "order-add" ? "New Order" : "Edit Order"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {actionError && <Alert variant="danger">{actionError}</Alert>}
          <Form>
            {modal === "order-add" ? (
              <Form.Group className="mb-3">
                <Form.Label>Size <span className="text-danger">*</span></Form.Label>
                <Form.Select value={form.size} onChange={field("size")}>
                  {BED_SIZES.map((s) => <option key={s} value={s}>{s}</option>)}
                </Form.Select>
              </Form.Group>
            ) : (
              <Form.Group className="mb-3">
                <Form.Label>Size</Form.Label>
                <Form.Control type="text" value={form.size ?? ""} readOnly plaintext className="fw-semibold" />
              </Form.Group>
            )}

            {isEditingFrames ? (
              <Form.Group className="mb-3">
                <Form.Label>Frames Ordered</Form.Label>
                <Form.Control
                  type="number" min="0"
                  value={form.frames_ordered ?? ""}
                  onChange={field("frames_ordered")}
                />
              </Form.Group>
            ) : (
              <Row className="g-2 mb-3">
                <Col>
                  <Form.Label>Mattresses Ordered</Form.Label>
                  <Form.Control
                    type="number" min="0"
                    value={form.mattresses_ordered ?? ""}
                    onChange={field("mattresses_ordered")}
                  />
                </Col>
                <Col>
                  <Form.Label>Box Springs Ordered</Form.Label>
                  <Form.Control
                    type="number" min="0"
                    value={form.box_springs_ordered ?? ""}
                    onChange={field("box_springs_ordered")}
                  />
                </Col>
              </Row>
            )}

            <Form.Group className="mb-3">
              <Form.Label>Date Ordered</Form.Label>
              <Form.Control
                type="date"
                value={form.date_ordered ?? ""}
                onChange={field("date_ordered")}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Source / Donor</Form.Label>
              <Form.Control
                type="text"
                placeholder="e.g. Walmart, USDA grant…"
                value={form.source ?? ""}
                onChange={field("source")}
              />
            </Form.Group>

            <Form.Group>
              <Form.Label>Notes</Form.Label>
              <Form.Control
                as="textarea" rows={2}
                value={form.notes ?? ""}
                onChange={field("notes")}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeModal} disabled={saving}>Cancel</Button>
          <Button variant="primary" onClick={handleSaveOrder} disabled={saving}>
            {saving ? <Spinner animation="border" size="sm" /> : "Save"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ── Mark Received Modal ── */}
      <Modal show={modal === "order-receive"} onHide={closeModal}>
        <Modal.Header closeButton>
          <Modal.Title>Mark Order Received — {target?.size}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {actionError && <Alert variant="danger">{actionError}</Alert>}
          <p className="text-muted small">
            Enter the actual quantities received. These are added to your running inventory.
          </p>
          {target?.size === "Frames" ? (
            <Form.Group className="mb-3">
              <Form.Label>Frames Received</Form.Label>
              <Form.Control
                type="number" min="0"
                value={form.frames_received ?? ""}
                onChange={field("frames_received")}
              />
            </Form.Group>
          ) : (
            <Row className="g-2 mb-3">
              <Col>
                <Form.Label>Mattresses Received</Form.Label>
                <Form.Control
                  type="number" min="0"
                  value={form.mattresses_received ?? ""}
                  onChange={field("mattresses_received")}
                />
              </Col>
              <Col>
                <Form.Label>Box Springs Received</Form.Label>
                <Form.Control
                  type="number" min="0"
                  value={form.box_springs_received ?? ""}
                  onChange={field("box_springs_received")}
                />
              </Col>
            </Row>
          )}
          <Form.Group>
            <Form.Label>Date Received</Form.Label>
            <Form.Control
              type="date"
              value={form.date_received ?? ""}
              onChange={field("date_received")}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeModal} disabled={saving}>Cancel</Button>
          <Button variant="success" onClick={handleReceiveOrder} disabled={saving}>
            {saving ? <Spinner animation="border" size="sm" /> : "Confirm Received"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ── Delete Confirm Modal ── */}
      <Modal show={modal === "order-delete"} onHide={closeModal}>
        <Modal.Header closeButton>
          <Modal.Title>Delete Order</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {actionError && <Alert variant="danger">{actionError}</Alert>}
          <p>
            Delete this {target?.size} order
            {target?.date_ordered ? ` from ${target.date_ordered}` : ""}?
          </p>
          {target?.date_received && (
            <Alert variant="warning" className="small">
              This order has already been received — deleting it will reduce your inventory counts.
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeModal} disabled={saving}>Cancel</Button>
          <Button variant="danger" onClick={handleDeleteOrder} disabled={saving}>
            {saving ? <Spinner animation="border" size="sm" /> : "Delete"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
